import {
  connectionOptionsFromFields,
  connectionTransports,
} from "../core/connection-prefs";
import { transportDefinition } from "../adapters/adapter";
import { transportSupported } from "../core/capabilities";
import { GrConnect } from "./gr-connect";

/**
 * Chrome shared by the endpoint blocks — the framed card, the source
 * picker, and the mono readout rows. `<gr-input>` and `<gr-output>` are
 * meant to read as a matched pair on the page, so they render from the
 * same stylesheet and the same picker builder.
 */

export const ENDPOINT_CSS = `
  :host {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--_border);
    border-radius: var(--_radius);
    background: rgba(0, 0, 0, 0.35);
    overflow: hidden;
  }
  :host([hidden]) { display: none !important; }
  header {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--_border);
  }
  .title {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .state {
    font-family: var(--_mono);
    font-size: 10px;
    color: var(--_muted);
    white-space: nowrap;
  }
  :host([data-state="connected"]) .state,
  :host([data-live]) .state { color: var(--_accent); }
  :host([data-state="error"]) .state,
  :host([data-state="mismatch"]) .state { color: #fb7185; }
  .body { display: flex; flex-direction: column; gap: 10px; padding: 12px 14px; }
  .pick { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
  .field { display: flex; align-items: center; gap: 6px; }
  select, input {
    border: 1px solid var(--_border);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: var(--_text);
    font-family: var(--_mono);
    font-size: 11px;
    padding: 7px 9px;
    outline: none;
    min-width: 0;
  }
  select { text-transform: lowercase; }
  input { width: 11ch; }
  input.wide { width: 20ch; flex: 1; }
  select:focus, input:focus { border-color: var(--_accent); }
  .rows {
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-height: 132px;
    overflow: auto;
    font-family: var(--_mono);
    font-size: 11px;
  }
  .row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
  .row .k { color: var(--_muted); white-space: nowrap; }
  .row .v { color: var(--_text); text-align: right; overflow-wrap: anywhere; }
  .row[data-stale] .v { color: var(--_muted); }
  .note { margin: 0; font-size: 10px; color: var(--_muted); }
  .note a { color: var(--_accent); }
  button.act {
    border: 1px solid var(--_border);
    border-radius: 8px;
    background: transparent;
    color: var(--_text);
    font-family: var(--_font);
    font-size: 9px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 7px 10px;
    cursor: pointer;
    white-space: nowrap;
  }
  button.act:hover:not(:disabled) { border-color: var(--_accent); background: rgba(255, 255, 255, 0.06); }
  button.act:disabled { opacity: 0.5; cursor: progress; }
  button.act[data-active] { border-color: var(--_accent); color: var(--_accent); }
`;

/** Attributes the block keeps for itself instead of handing to <gr-connect>. */
const BLOCK_ONLY = new Set(["label", "signals", "class", "style", "id", "hidden", "title", "canvas", "screen"]);

export interface SourcePicker {
  element: HTMLElement;
  connect: GrConnect;
}

/**
 * Build the "select a source, then connect" row: a transport dropdown with
 * that transport's settings inline, followed by the `<gr-connect>` pill that
 * owns the actual connection. Changing the picker writes the same
 * preferences the connect pill's own switcher uses (credentials stay in the
 * live pill), so the two never disagree — and never auto-connects, because WebSerial needs the
 * click to come from the user.
 */
export function buildSourcePicker(host: HTMLElement, buttonLabel: string): SourcePicker {
  const element = document.createElement("div");
  element.className = "pick";

  const select = document.createElement("select");
  select.name = "transport";
  select.setAttribute("aria-label", "Source protocol");
  const fields = document.createElement("span");
  fields.className = "pick";

  const connect = document.createElement("gr-connect") as GrConnect;
  for (const attribute of Array.from(host.attributes)) {
    if (BLOCK_ONLY.has(attribute.name.toLowerCase())) continue;
    connect.setAttribute(attribute.name, attribute.value);
  }
  connect.setAttribute("label", buttonLabel);
  connect.setAttribute("switcher", "off");

  element.append(select, fields, connect);

  let renderedFor = "";

  const renderFields = (transport: string): void => {
    const options = connect.connectionSettings().options;
    renderedFor = transport;
    fields.textContent = "";
    for (const field of transportDefinition(transport)?.fields ?? []) {
      const input = document.createElement("input");
      input.name = field.name;
      input.type = field.type ?? "text";
      input.autocomplete = "off";
      input.spellcheck = false;
      input.placeholder = field.placeholder;
      input.title = field.label;
      input.setAttribute("aria-label", field.label);
      input.value = options[field.name] ?? "";
      if (field.name !== "baud") input.className = "wide";
      input.addEventListener("change", () => void commit());
      fields.appendChild(input);
    }
  };

  const commit = async (): Promise<void> => {
    // Fields still showing the previous protocol's settings aren't its own.
    const options = renderedFor === select.value
      ? connectionOptionsFromFields(select.value, fields.querySelectorAll("input"))
      : {};
    await connect.setConnectionOverride({
      transport: select.value,
      options,
    });
    renderFields(select.value);
  };

  select.addEventListener("change", () => void commit());

  // The element has to be connected before it can report its stored override.
  queueMicrotask(() => {
    const active = connect.connectionSettings().transport;
    const offered: Array<{ name: string; label?: string }> = connectionTransports();
    if (!transportDefinition(active)) offered.unshift({ name: active });
    select.textContent = "";
    for (const definition of offered) {
      const option = document.createElement("option");
      option.value = definition.name;
      option.selected = definition.name === active;
      option.disabled = !transportSupported(definition.name);
      const label = definition.label ?? definition.name;
      option.textContent = transportSupported(definition.name) ? label : `${label} (unavailable)`;
      select.appendChild(option);
    }
    renderFields(active);
  });

  return { element, connect };
}

/** Replace the readout rows in one pass — key on the left, value on the right. */
export function renderRows(
  container: HTMLElement,
  rows: Array<{ key: string; value: string; stale?: boolean }>,
  empty: string,
): void {
  container.textContent = "";
  if (rows.length === 0) {
    const note = document.createElement("span");
    note.className = "note";
    note.textContent = empty;
    container.appendChild(note);
    return;
  }
  for (const entry of rows) {
    const row = document.createElement("div");
    row.className = "row";
    row.toggleAttribute("data-stale", entry.stale === true);
    const key = document.createElement("span");
    key.className = "k";
    key.textContent = entry.key;
    const value = document.createElement("span");
    value.className = "v";
    value.textContent = entry.value;
    row.append(key, value);
    container.appendChild(row);
  }
}
